import { useState } from "react";
import {
  CalendarClock, AlertCircle, Clock, CheckCircle,
  Upload, ExternalLink, Edit3, RefreshCw, Sparkles,
} from "lucide-react";
import { getUrgency, formatExpiryDate, findRenewalLink } from "../deadlineUtils";
import ExpiryDatePicker from "./ExpiryDatePicker";
import EvidenceCurrencyTimeline from "./EvidenceCurrencyTimeline";

const APPRAISAL_ITEM_IDS = ["a_ap1","a_ap2","a_ap3","a_ap4","a_ap5"];

export default function DeadlinesView({
  deadlines,         // array of deadline records from Supabase
  specialty,
  specialtyData,     // SPECIALTIES[specialty]
  evidence,          // evidence store for this specialty
  user,
  themeMode,
  onCreateDeadline,  // (itemId, domainId, expiryInfo) => Promise
  onRemoveDeadline,  // (deadlineId) => Promise
  onMarkRenewed,     // (deadlineId) => Promise
  onNavToItem,       // (domainId) => void — navigate to domain panel
}) {
  const isLight = themeMode === "clinical_light";
  const t = {
    appBg:    isLight ? "#F8FBFF" : "#0F1F2E",
    headText: isLight ? "#1F2D3D" : "#F3F7FF",
    bodyText: isLight ? "#5F738A" : "#94A3B8",
    muted:    isLight ? "#94A3B8" : "#64748B",
    cardBg:   isLight ? "#FFFFFF" : "#162e4a",
    cardBrd:  isLight ? "#E2E8F0" : "#224068",
    panelBg:  isLight ? "#FFFFFF" : "#162e4a",
    panelBrd: isLight ? "#E2E8F0" : "#213045",
    tabBg:    isLight ? "#F1F5F9" : "#1b3355",
    tabBrd:   isLight ? "#E2E8F0" : "#213045",
  };

  const [filter, setFilter] = useState("all"); // all | expired | soon | upcoming | completed
  const [editingDeadlineId, setEditingDeadlineId] = useState(null);

  // Sort deadlines by urgency
  const sorted = [...deadlines]
    .filter(d => d.status !== "removed")
    .sort((a, b) => {
      const ua = getUrgency(a.expiry_date);
      const ub = getUrgency(b.expiry_date);
      return (ua?.daysUntil ?? 9999) - (ub?.daysUntil ?? 9999);
    });

  const filteredDeadlines = sorted.filter(d => {
    const u = getUrgency(d.expiry_date);
    if (filter === "all")       return d.status === "active";
    if (filter === "expired")   return d.status === "active" && (u?.level === "expired" || u?.level === "critical");
    if (filter === "soon")      return d.status === "active" && (u?.level === "urgent" || u?.level === "soon");
    if (filter === "upcoming")  return d.status === "active" && u?.level === "ok";
    if (filter === "completed") return d.status === "renewed";
    return true;
  });

  // Summary stats
  const active = sorted.filter(d => d.status === "active");
  const expiredCount = active.filter(d => {
    const u = getUrgency(d.expiry_date);
    return u?.level === "expired" || u?.level === "critical";
  }).length;
  const soonCount = active.filter(d => {
    const u = getUrgency(d.expiry_date);
    return u?.level === "urgent" || u?.level === "soon";
  }).length;
  const onTrackCount = active.filter(d => getUrgency(d.expiry_date)?.level === "ok").length;

  // Appraisal timeline
  const appraisalItems = APPRAISAL_ITEM_IDS
    .map((id, idx) => {
      const ev = evidence[id] || {};
      const dl = deadlines.find(d => d.item_id === id && d.status === "active");
      return { id, yearLabel: `Year ${idx + 1}${idx === 0 ? " (most recent)" : idx === 4 ? " (oldest)" : ""}`, ev, deadline: dl };
    });

  const mostRecentAppraisal = appraisalItems.find(a => a.ev?.fileName || a.deadline);

  const filterTabs = [
    { key: "all",       label: "All",              count: active.length },
    { key: "expired",   label: "Expired",          count: expiredCount },
    { key: "soon",      label: "Due soon (12w)",   count: soonCount },
    { key: "upcoming",  label: "Upcoming",         count: onTrackCount },
    { key: "completed", label: "Completed",        count: sorted.filter(d => d.status === "renewed").length },
  ];

  return (
    <div style={{ maxWidth: 860 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(47,175,159,.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CalendarClock size={18} style={{ color: "#2FAF9F" }} />
          </div>
          <div>
            <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 22, fontWeight: 700, color: t.headText, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
              Deadlines &amp; Renewals
            </h1>
            <p style={{ fontSize: 12.5, color: t.bodyText, marginTop: 2 }}>
              Certificates, training, and appraisals that need renewing before you submit
            </p>
          </div>
        </div>
      </div>

      {/* Summary stat cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
        <StatCard label="Expired" count={expiredCount} color="#ef4444" bg="rgba(239,68,68,.08)" brd="rgba(239,68,68,.2)" icon={AlertCircle} />
        <StatCard label="Due within 12 weeks" count={soonCount} color="#f59e0b" bg="rgba(245,158,11,.08)" brd="rgba(245,158,11,.2)" icon={Clock} />
        <StatCard label="On track" count={onTrackCount} color="#10b981" bg="rgba(16,185,129,.08)" brd="rgba(16,185,129,.2)" icon={CheckCircle} />
      </div>

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {filterTabs.map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            style={{
              padding: "6px 14px",
              borderRadius: 20,
              border: `1px solid ${filter === tab.key ? "#2FAF9F" : t.tabBrd}`,
              background: filter === tab.key ? "rgba(47,175,159,.12)" : t.tabBg,
              color: filter === tab.key ? "#2FAF9F" : t.bodyText,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: filter === tab.key ? 700 : 500,
              fontFamily: "inherit",
              display: "flex",
              alignItems: "center",
              gap: 6,
              transition: "all .13s",
            }}
          >
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                fontSize: 9.5,
                fontWeight: 700,
                padding: "1px 5px",
                borderRadius: 10,
                background: filter === tab.key ? "#2FAF9F" : t.cardBrd,
                color: filter === tab.key ? "#fff" : t.muted,
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Deadline list — primary content */}
      {filteredDeadlines.length === 0 ? (
        <EmptyState filter={filter} t={t} />
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 28 }}>
          {filteredDeadlines.map(deadline => (
            <DeadlineCard
              key={deadline.id}
              deadline={deadline}
              themeMode={themeMode}
              t={t}
              isEditing={editingDeadlineId === deadline.id}
              onEdit={() => setEditingDeadlineId(editingDeadlineId === deadline.id ? null : deadline.id)}
              onSaveEdit={async (data) => {
                await onCreateDeadline(deadline.item_id, deadline.domain_id, {
                  ...data,
                  source: deadline.source,
                  expiryLabel: deadline.expiry_label,
                  expiryType: deadline.expiry_type,
                });
                setEditingDeadlineId(null);
              }}
              onRemove={async () => {
                await onRemoveDeadline(deadline.id);
                setEditingDeadlineId(null);
              }}
              onMarkRenewed={() => onMarkRenewed?.(deadline.id)}
              onUpload={() => onNavToItem?.(deadline.domain_id)}
            />
          ))}
        </div>
      )}

      {/* GMC evidence currency timeline — secondary, only shown if data exists */}
      <EvidenceCurrencyTimeline
        deadlines={deadlines}
        specialtyData={specialtyData}
        themeMode={themeMode}
        t={t}
        onNavToItem={onNavToItem}
      />

      {/* Divider */}
      <div style={{ borderTop: `1px solid ${t.cardBrd}`, margin: "28px 0 20px", display: "flex", alignItems: "center", gap: 10 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: t.muted, letterSpacing: ".1em", textTransform: "uppercase", background: t.appBg, padding: "0 8px", marginTop: -9, marginLeft: 8 }}>Annual appraisals</span>
      </div>

      {/* Annual appraisals section — at the bottom */}
      <AppraisalTimeline
        items={appraisalItems}
        mostRecent={mostRecentAppraisal}
        themeMode={themeMode}
        t={t}
        onNav={() => onNavToItem?.("appraisals")}
      />
    </div>
  );
}

// ─── Appraisal Timeline ───────────────────────────────────────────────────────
function AppraisalTimeline({ items, mostRecent, themeMode, t, onNav }) {
  const isLight = themeMode === "clinical_light";
  const uploadedCount = items.filter(a => a.ev?.fileName || a.deadline).length;

  // Compute next appraisal due
  let nextDue = null;
  if (mostRecent?.deadline?.expiry_date) {
    const d = new Date(mostRecent.deadline.expiry_date + 'T00:00:00');
    nextDue = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } else if (mostRecent?.ev?.fileName) {
    // Can't compute without a date — prompt to add
    nextDue = "Set expiry date to calculate";
  }

  const urgency = mostRecent?.deadline ? getUrgency(mostRecent.deadline.expiry_date) : null;

  return (
    <div style={{
      background: t.cardBg,
      border: `1px solid ${t.cardBrd}`,
      borderRadius: 12,
      padding: "14px 18px",
      marginBottom: 20,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: t.headText, marginBottom: 2 }}>Annual appraisals — 5 required</div>
          <div style={{ fontSize: 11, color: t.muted }}>
            {uploadedCount}/5 uploaded
            {nextDue && (
              <span style={{ marginLeft: 10, color: urgency?.color || t.muted, fontWeight: 600 }}>
                · Next due: {nextDue}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={onNav}
          style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid #2FAF9F40", background: "rgba(47,175,159,.08)", color: "#2FAF9F", cursor: "pointer", fontSize: 11, fontWeight: 600, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 5 }}
        >
          <Upload size={10} /> Upload appraisals
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {items.map((a, idx) => {
          const hasFile = a.ev?.fileName;
          const hasDl   = a.deadline;
          const filled  = hasFile || hasDl;
          const u       = hasDl ? getUrgency(a.deadline.expiry_date) : null;

          return (
            <div
              key={a.id}
              style={{
                flex: "1 1 140px",
                minWidth: 130,
                background: isLight ? "#F8FAFC" : "#1b3355",
                border: `1px solid ${filled ? (u?.color ? u.color + "40" : "#2FAF9F40") : t.cardBrd}`,
                borderRadius: 8,
                padding: "8px 10px",
              }}
            >
              <div style={{ fontSize: 9.5, fontWeight: 700, color: t.muted, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 3 }}>
                {a.yearLabel}
              </div>
              {filled ? (
                <>
                  <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <CheckCircle size={11} style={{ color: "#10b981", flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: t.headText, fontWeight: 600 }}>Uploaded</span>
                  </div>
                  {hasDl && (
                    <div style={{ fontSize: 10, color: u?.color || t.muted, marginTop: 2 }}>
                      {formatExpiryDate(a.deadline.expiry_date)}
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 11, color: t.muted, fontStyle: "italic" }}>Not yet uploaded</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, count, color, bg, brd, icon: Icon }) {
  return (
    <div style={{ background: bg, border: `1px solid ${brd}`, borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
      <Icon size={18} style={{ color, flexShrink: 0 }} />
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "'DM Serif Display',serif", lineHeight: 1 }}>{count}</div>
        <div style={{ fontSize: 10.5, color, opacity: 0.8, marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

// ─── Deadline card ────────────────────────────────────────────────────────────
function DeadlineCard({ deadline, themeMode, t, isEditing, onEdit, onSaveEdit, onRemove, onMarkRenewed, onUpload }) {
  const u = getUrgency(deadline.expiry_date);
  const renewalLink = findRenewalLink(deadline.item_label);

  const domNum = deadline.domain_id
    ? deadline.domain_label?.match(/DOM (\d+)/)?.[0] || deadline.domain_label?.slice(0, 12)
    : "";

  return (
    <div style={{ overflow: "hidden", borderRadius: 10, border: `1px solid ${u?.borderColor || t.cardBrd}`, background: u?.bgColor || t.cardBg }}>
      <div style={{ display: "flex", gap: 0 }}>
        {/* Urgency bar */}
        <div style={{ width: 4, background: u?.barColor || "#7a9ab8", borderRadius: "10px 0 0 10px", flexShrink: 0 }} />

        <div style={{ flex: 1, padding: "12px 14px" }}>
          {/* Top row */}
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 6, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 3 }}>
                {deadline.domain_label && (
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(47,175,159,.12)", color: "#2FAF9F", border: "1px solid rgba(47,175,159,.2)", fontFamily: "monospace" }}>
                    {domNum}
                  </span>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: t.headText }}>{deadline.item_label}</span>
                {deadline.source === "ai_detected" && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "rgba(47,175,159,.1)", color: "#2FAF9F", border: "1px solid rgba(47,175,159,.2)" }}>
                    AI detected
                  </span>
                )}
              </div>
              {deadline.expiry_label && deadline.expiry_label !== deadline.item_label && (
                <div style={{ fontSize: 11, color: t.bodyText, marginBottom: 2 }}>{deadline.expiry_label}</div>
              )}
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, fontWeight: 600, color: u?.color || t.muted }}>
                  Expires {formatExpiryDate(deadline.expiry_date)}
                </span>
                <span style={{
                  fontSize: 10.5,
                  fontWeight: u?.level === "expired" || u?.level === "critical" ? 700 : 500,
                  color: u?.color || t.muted,
                }}>
                  {u?.label}
                </span>
              </div>
              {deadline.notes && (
                <div style={{ fontSize: 10.5, color: t.muted, marginTop: 3 }}>📝 {deadline.notes}</div>
              )}
            </div>

            {/* Status badge */}
            <span style={{
              fontSize: 10,
              fontWeight: 700,
              padding: "2px 8px",
              borderRadius: 20,
              background: u?.bgColor || "rgba(71,85,105,.1)",
              color: u?.color || t.muted,
              border: `1px solid ${u?.borderColor || t.cardBrd}`,
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}>
              {u?.shortLabel || "—"}
            </span>
          </div>

          {/* Action buttons */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
            <ActionBtn icon={<Upload size={10} />} label="Upload renewed" color={u?.color || "#2FAF9F"} onClick={onUpload} />
            <ActionBtn icon={<Edit3 size={10} />} label="Edit date" color="#7a9ab8" onClick={onEdit} active={isEditing} />
            {deadline.status === "active" && (
              <ActionBtn icon={<RefreshCw size={10} />} label="Mark renewed" color="#10b981" onClick={onMarkRenewed} />
            )}
            {renewalLink && (
              <a
                href={renewalLink.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 10px",
                  borderRadius: 6,
                  border: "1px solid rgba(14,165,233,.3)",
                  background: "rgba(14,165,233,.07)",
                  color: "#38bdf8",
                  textDecoration: "none",
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                <ExternalLink size={9} /> {renewalLink.label}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Inline edit panel */}
      {isEditing && (
        <div style={{ borderTop: `1px solid ${t.cardBrd}` }}>
          <ExpiryDatePicker
            item={{ label: deadline.item_label, id: deadline.item_id }}
            deadline={deadline}
            onSave={onSaveEdit}
            onRemove={onRemove}
            onClose={() => onEdit()}
            themeMode={themeMode}
          />
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon, label, color, onClick, active = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "4px 10px",
        borderRadius: 6,
        border: `1px solid ${active ? color + "60" : color + "35"}`,
        background: active ? color + "18" : color + "0d",
        color,
        cursor: "pointer",
        fontSize: 11,
        fontWeight: 600,
        fontFamily: "inherit",
        whiteSpace: "nowrap",
        transition: "all .12s",
      }}
      onMouseEnter={e => { e.currentTarget.style.background = color + "20"; e.currentTarget.style.borderColor = color + "60"; }}
      onMouseLeave={e => { e.currentTarget.style.background = active ? color + "18" : color + "0d"; e.currentTarget.style.borderColor = active ? color + "60" : color + "35"; }}
    >
      {icon} {label}
    </button>
  );
}

function EmptyState({ filter, t }) {
  const msgs = {
    all:       { icon: <CalendarClock size={28} style={{ color: "#2FAF9F" }} />, title: "No deadlines tracked yet", sub: "Upload certificates and we'll detect expiry dates automatically, or use the calendar button on any evidence item to set one manually." },
    expired:   { icon: <CheckCircle size={28} style={{ color: "#10b981" }} />, title: "No expired certificates", sub: "Great — nothing is expired." },
    soon:      { icon: <CheckCircle size={28} style={{ color: "#10b981" }} />, title: "Nothing due within 12 weeks", sub: "You're ahead of your renewals." },
    upcoming:  { icon: <Sparkles size={28} style={{ color: "#2FAF9F" }} />, title: "Nothing upcoming", sub: "Set expiry dates on your certificates to track them here." },
    completed: { icon: <CheckCircle size={28} style={{ color: "#10b981" }} />, title: "No completed renewals", sub: "Mark a deadline as renewed to track your completed renewals." },
  };
  const m = msgs[filter] || msgs.all;
  return (
    <div style={{ textAlign: "center", padding: "48px 24px", color: t.muted }}>
      <div style={{ marginBottom: 10 }}>{m.icon}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: t.headText, marginBottom: 6 }}>{m.title}</div>
      <div style={{ fontSize: 12, color: t.muted, maxWidth: 400, margin: "0 auto", lineHeight: 1.6 }}>{m.sub}</div>
    </div>
  );
}
